import type { View } from '../../../types/forge';

/**
 * Drop-target resolution for palette → canvas drops.
 *
 * The canvas can't see the iframe's hover target precisely, so we use
 * the editor's current selection as a hint and walk up/down the view
 * tree to find the most-correct container for the dragged type.
 *
 * Two component classes drive different placement rules:
 *
 *   ROUTE_TYPES   — Screen / StackNavigator / TabNavigator
 *                   Must go under a navigator (or replace the empty
 *                   root). Walk UP from selection to the nearest
 *                   navigator ancestor.
 *
 *   CONTENT_TYPES — everything else (View, Text, Button, …)
 *                   Must go under a content container, NOT under a
 *                   navigator (navigators only accept routes). Walk
 *                   UP from selection to the nearest content-container
 *                   ancestor; if the selection is inside a navigator
 *                   chain with no content container, fall back to the
 *                   root navigator's first Screen descendant.
 *
 * The previous resolver checked a small `containerTypes` set that
 * forgot `Screen` entirely — selecting a Screen always fell through to
 * `rootId`, so dropped views landed as siblings of the navigator's
 * Screens instead of inside the selected screen. This module fixes
 * that and adds the navigator-aware fallback.
 */

const ROUTE_TYPES: ReadonlySet<string> = new Set([
  'Screen',
  'StackNavigator',
  'TabNavigator',
]);

const NAVIGATOR_TYPES: ReadonlySet<string> = new Set([
  'StackNavigator',
  'TabNavigator',
]);

/**
 * Views that legitimately accept arbitrary content as children.
 * Notably: `Screen` belongs here. Navigators do NOT — they only accept
 * other routes (and overlays — see OVERLAY_TYPES).
 */
const CONTENT_CONTAINER_TYPES: ReadonlySet<string> = new Set([
  'View',
  'ScrollView',
  'Stack',
  'Pressable',
  'Tab',
  'Screen',
]);

/**
 * View types that render as ABSOLUTE overlays above their parent's
 * content (currently: just `Drawer`). They behave like content for
 * placement purposes — but unlike normal content they're also welcome
 * inside a navigator: the engine's StackNavigator / TabNavigator
 * extract overlay children and render them as siblings of the route
 * stack via `pickOverlayChildren`. Dropping a Drawer onto a navigator
 * therefore means "this drawer overlays every screen of this navigator"
 * — the canonical hamburger-menu pattern.
 */
const OVERLAY_TYPES: ReadonlySet<string> = new Set(['Drawer']);

interface ViewLite {
  id: string;
  Type: string;
  child_views?: View['child_views'];
}

/**
 * Compute the parent id under which to insert a fresh view of
 * `draggedType`. Returns `null` when there's no main_view yet (empty
 * app) — the caller treats null as "use this view as main_view_id".
 *
 * - `selectedId` is the editor's selection at drop time. May be null.
 * - `rootId` is the FrontendApp's main_view_id. Treated as the global
 *   fallback when selection doesn't yield a useful container.
 * - `views` is the flat list of every View in the app — used to build
 *   the parent-of-child map and to inspect Type / children.
 */
export function resolveDropParent(
  draggedType: string,
  selectedId: string | null,
  rootId: string | null,
  views: View[],
): string | null {
  // Empty app — the new view becomes main_view, no parent edge needed.
  if (!rootId) return null;

  const viewsById = new Map<string, ViewLite>(
    views.map((v) => [v.id, { id: v.id, Type: v.Type, child_views: v.child_views }]),
  );

  // Build a single-parent map. With our tree contract a child has at
  // most one parent edge; if the schema ever drifts we use the first
  // parent we see (deterministic but not necessarily correct under
  // M:N).
  const parentByChild = new Map<string, string>();
  for (const v of views) {
    for (const edge of v.child_views ?? []) {
      if (!parentByChild.has(edge.related_view_id)) {
        parentByChild.set(edge.related_view_id, v.id);
      }
    }
  }

  const isRoute = ROUTE_TYPES.has(draggedType);
  const isOverlay = OVERLAY_TYPES.has(draggedType);

  if (isRoute) {
    // Walk UP to find the nearest navigator ancestor (inclusive of self).
    let cursor: string | null = selectedId;
    while (cursor) {
      const v = viewsById.get(cursor);
      if (!v) break;
      if (NAVIGATOR_TYPES.has(v.Type)) return v.id;
      cursor = parentByChild.get(cursor) ?? null;
    }
    // Fall back to root if it's a navigator. Otherwise we still
    // attempt rootId — the caller's mutation will succeed (the schema
    // permits any parent / child types), but the engine may choose to
    // skip rendering invalid combinations. This is a "best effort"
    // safety net.
    const root = viewsById.get(rootId);
    if (root && NAVIGATOR_TYPES.has(root.Type)) return rootId;
    return rootId;
  }

  if (isOverlay) {
    // Drawer / overlay drops accept BOTH content containers (Screen,
    // View, etc.) AND navigators as parents. Walking from selection
    // up, we drop into the first ancestor of either kind. So
    // selecting a navigator and dropping a Drawer puts the Drawer on
    // the navigator (overlays every screen); selecting a screen
    // puts it on that screen (overlays only that screen). Without
    // this branch the resolver would walk past navigators looking
    // for a content container and silently land deep inside the
    // first screen's tree.
    let cursor: string | null = selectedId;
    while (cursor) {
      const v = viewsById.get(cursor);
      if (!v) break;
      if (CONTENT_CONTAINER_TYPES.has(v.Type) || NAVIGATOR_TYPES.has(v.Type)) {
        return v.id;
      }
      cursor = parentByChild.get(cursor) ?? null;
    }
    // No selection — drop on root regardless of type. The engine
    // tolerates an overlay at any container.
    return rootId;
  }

  // Content type. Walk UP to nearest content container ancestor.
  let cursor: string | null = selectedId;
  while (cursor) {
    const v = viewsById.get(cursor);
    if (!v) break;
    if (CONTENT_CONTAINER_TYPES.has(v.Type)) return v.id;
    cursor = parentByChild.get(cursor) ?? null;
  }

  // Selection chain didn't yield a content container — typical case:
  // user has a navigator selected (or nothing selected) in an app
  // whose root is a navigator. Try to descend into the root navigator
  // and find the first Screen descendant, which is the most natural
  // landing spot for content.
  const root = viewsById.get(rootId);
  if (!root) return rootId;
  if (CONTENT_CONTAINER_TYPES.has(root.Type)) return rootId;
  if (NAVIGATOR_TYPES.has(root.Type)) {
    const firstScreen = findFirstScreenDescendant(rootId, viewsById);
    if (firstScreen) return firstScreen;
  }
  return rootId;
}

function findFirstScreenDescendant(
  startId: string,
  viewsById: Map<string, ViewLite>,
): string | null {
  // BFS so we pick the *closest* Screen, not an arbitrary deeply-
  // nested one. Sort children by `sort` so traversal mirrors the
  // navigator's declared order.
  const queue: string[] = [startId];
  const seen = new Set<string>();
  while (queue.length > 0) {
    const id = queue.shift()!;
    if (seen.has(id)) continue;
    seen.add(id);
    const v = viewsById.get(id);
    if (!v) continue;
    if (id !== startId && v.Type === 'Screen') return id;
    const sorted = [...(v.child_views ?? [])].sort((a, b) => a.sort - b.sort);
    for (const edge of sorted) queue.push(edge.related_view_id);
  }
  return null;
}
